import { useEffect, useRef, useState, useCallback } from "react";
import { io, Socket } from "socket.io-client";
import { playSound, WAITER_SOUNDS, ORDER_SOUNDS } from "@/utils/notification-sounds";
import { getTrendyolgoRecentOrders } from "@/api/endpoints";
import { printOrderReceipt } from "@/utils/print-receipt";

/** Otomatik fiş yazdırma açık mı (Trendyol GO sayfasından aç/kapat). */
function autoPrintEnabled(): boolean {
  return localStorage.getItem("trendyolgo-autoprint") !== "0";
}

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:3004";

export interface WaiterCall {
  table: string;
  venueName: string;
  timestamp: number;
  id: string;
}

export interface OrderNotification {
  table: string;
  timestamp: number;
  id: string;
}

export function useWaiterCalls(
  venueIds: string[],
  waiterSoundKey = "bell",
  orderSoundKey = "kaching"
) {
  const [calls, setCalls] = useState<WaiterCall[]>([]);
  const [newOrders, setNewOrders] = useState<OrderNotification[]>([]);
  const socketRef = useRef<Socket | null>(null);
  const waiterSoundRef = useRef(waiterSoundKey);
  const orderSoundRef = useRef(orderSoundKey);
  // Aynı siparişi iki kez yazdırmayı önler (orderId bazlı).
  const printedRef = useRef<Set<string>>(new Set());

  // Keep refs in sync
  waiterSoundRef.current = waiterSoundKey;
  orderSoundRef.current = orderSoundKey;

  useEffect(() => {
    if (venueIds.length === 0) return;

    const socket = io(API_BASE, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
    });
    socketRef.current = socket;

    const joinRooms = () => {
      for (const id of venueIds) {
        socket.emit("join:venue", id);
      }
    };

    // Join on connect and re-join on reconnect
    joinRooms();
    socket.on("connect", joinRooms);

    socket.on("waiter:call", (data: { table: string; venueName: string; timestamp: number }) => {
      const call: WaiterCall = {
        ...data,
        id: `${data.timestamp}-${Math.random().toString(36).slice(2, 6)}`,
      };

      setCalls((prev) => [call, ...prev].slice(0, 50));
      playSound(WAITER_SOUNDS, waiterSoundRef.current);

      if (Notification.permission === "granted") {
        new Notification(`🔔 ${data.table}`, {
          body: `${data.venueName} - Garson cagrisi`,
          tag: call.id,
          silent: false,
        } as NotificationOptions);
      }
    });

    socket.on("order:new", (data: Record<string, unknown>) => {
      const table = (data?.table as string) || "Masa";
      const notif: OrderNotification = {
        table,
        timestamp: Date.now(),
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      };

      setNewOrders((prev) => [notif, ...prev].slice(0, 50));
      playSound(ORDER_SOUNDS, orderSoundRef.current);

      if (Notification.permission === "granted") {
        new Notification(`🛒 ${table}`, {
          body: "Yeni siparis geldi",
          tag: notif.id,
          silent: false,
        } as NotificationOptions);
      }

      // Trendyol GO siparişlerinde otomatik termal fiş bastır. Socket payload'u
      // ürün detayını taşımadığı için siparişi recent-orders'tan çekip basarız.
      const source = data?.source as string | undefined;
      const venueId = data?.venueId as string | undefined;
      const orderId = data?.orderId as string | undefined;
      if (
        source === "trendyolgo" &&
        venueId &&
        orderId &&
        autoPrintEnabled() &&
        !printedRef.current.has(orderId)
      ) {
        printedRef.current.add(orderId);
        getTrendyolgoRecentOrders(venueId)
          .then((orders) => {
            const order = orders.find((o) => (o.id || o._id) === orderId);
            if (order) printOrderReceipt(order);
          })
          .catch(() => {
            // Çekilemezse otomatik baskı sessizce atlanır; kullanıcı sayfadan
            // "Yazdır" ile manuel basabilir.
            printedRef.current.delete(orderId);
          });
      }
    });

    return () => {
      for (const id of venueIds) {
        socket.emit("leave:venue", id);
      }
      socket.disconnect();
    };
  }, [venueIds.join(",")]);

  const dismissCall = useCallback((id: string) => {
    setCalls((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setCalls([]);
  }, []);

  const dismissOrder = useCallback((id: string) => {
    setNewOrders((prev) => prev.filter((o) => o.id !== id));
  }, []);

  const dismissAllOrders = useCallback(() => {
    setNewOrders([]);
  }, []);

  const requestNotificationPermission = useCallback(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  return {
    calls, dismissCall, dismissAll,
    newOrders, dismissOrder, dismissAllOrders,
    requestNotificationPermission,
  };
}
